import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react';
import { TOAST_EVENT, ToastPayload, ToastType } from '../utils/toast';
import { AUTH_LOGOUT_EVENT } from '../lib/request';

interface ToastItem extends ToastPayload {
  id: string;
  type: ToastType;
  duration: number;
}

const iconMap: Record<ToastType, React.ElementType> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const toastStyles: Record<ToastType, string> = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  error: 'border-red-200 bg-red-50 text-red-800',
  warning: 'border-amber-200 bg-amber-50 text-amber-800',
  info: 'border-sky-200 bg-sky-50 text-sky-800',
};

const iconStyles: Record<ToastType, string> = {
  success: 'text-emerald-500',
  error: 'text-red-500',
  warning: 'text-amber-500',
  info: 'text-sky-500',
};

export const ToastProvider: React.FC = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const pushToast = useCallback((payload: ToastPayload) => {
    const message = String(payload.message ?? '').trim();
    if (!message) return;

    const toast: ToastItem = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      message,
      type: payload.type || 'info',
      duration: payload.duration ?? 3200,
      onClick: payload.onClick,
    };

    setToasts((current) => [...current.slice(-3), toast]);
    window.setTimeout(() => removeToast(toast.id), toast.duration);
  }, [removeToast]);

  useEffect(() => {
    const handleToast = (event: Event) => {
      const detail = (event as CustomEvent<ToastPayload>).detail;
      if (detail) pushToast(detail);
    };
    const handleAuthLogout = () => setToasts([]);

    window.addEventListener(TOAST_EVENT, handleToast);
    window.addEventListener(AUTH_LOGOUT_EVENT, handleAuthLogout);
    return () => {
      window.removeEventListener(TOAST_EVENT, handleToast);
      window.removeEventListener(AUTH_LOGOUT_EVENT, handleAuthLogout);
    };
  }, [pushToast]);

  useEffect(() => {
    const nativeAlert = window.alert;
    window.alert = (message?: unknown) => {
      pushToast({ type: 'info', message: String(message ?? '') });
    };

    return () => {
      window.alert = nativeAlert;
    };
  }, [pushToast]);

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed left-1/2 top-6 z-[10000] flex -translate-x-1/2 flex-col items-center gap-3 pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((toast) => {
        const Icon = iconMap[toast.type];

        return (
          <div
            key={toast.id}
            className={`toast-card pointer-events-auto flex min-w-[320px] max-w-[min(92vw,440px)] items-center gap-3 rounded-xl border px-4 py-3 shadow-lg ${toast.onClick ? 'cursor-pointer hover:brightness-[0.98]' : ''} ${toastStyles[toast.type]}`}
            role="status"
            onClick={toast.onClick ? () => {
              toast.onClick?.();
              removeToast(toast.id);
            } : undefined}
          >
            <Icon className={`h-5 w-5 flex-shrink-0 ${iconStyles[toast.type]}`} />
            <div className="flex-1 text-sm font-semibold leading-5">{toast.message}</div>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                removeToast(toast.id);
              }}
              className="rounded-lg p-1 text-current/55 transition-colors hover:bg-black/5 hover:text-current focus:outline-none focus:ring-2 focus:ring-current/20"
              aria-label="关闭提示"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
};
